import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Spinner } from "@fluentui/react-components";
import { vorgangService } from "../services/vorgangService";
import { todoSprungPath } from "./todoDetailBacklink";
import "../components/vorgang/detail/vorgangDetail.css";

/**
 * Weiterleitung `/todos/:id` → `/vorgaenge/:vorgangId?todo=:id`.
 *
 * To-dos haben KEINE eigene Detailseite mehr — die Ausklappung in der
 * Todos-Karte des Vorgangs IST die Detailansicht. Die Route bleibt trotzdem
 * ZWINGEND bestehen, weil `/todos/:id` als Deep-Link persistiert ist:
 *  - in der `Notification`-Tabelle (Alt-Bestand aus todo-rollover/todo-note),
 *  - und — nicht mehr korrigierbar — in bereits ZUGESTELLTEN Web-Push-
 *    Nachrichten, deren Klick über NavigationLayout ungeprüft navigiert.
 * Neue Mitteilungen zeigen bereits direkt auf `/vorgaenge/:vorgangId?todo=…`.
 *
 * Die Weiterleitung ersetzt den History-Eintrag (`replace: true`), damit der
 * Zurück-Button nicht in die Redirect-Schleife zurückführt.
 */
const TodoRedirectPage: React.FC = () => {
    const { id: idStr } = useParams();
    const id = Number(idStr);
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!Number.isFinite(id)) {
            setError("Ungültige To-do-Nummer.");
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const todo = await vorgangService.getTodo(id, { force: true });
                if (cancelled) return;
                // Mit `?todo=` klappt das To-do im Vorgang gleich auf (User 01.11.2026).
                const ziel = todoSprungPath(todo, id);
                if (ziel) navigate(ziel, { replace: true });
                // Ein To-do ohne Eltern-Vorgang dürfte es nicht geben (jeder
                // Create legt einen Wrapper-Vorgang an, der Bestand ist per
                // Migration backfilled). Falls doch: sauber melden statt auf
                // `/vorgaenge/undefined` zu landen.
                else setError("Zu diesem To-do lässt sich kein Vorgang bestimmen.");
            } catch (err: any) {
                if (cancelled) return;
                console.error("[TodoRedirect] Laden fehlgeschlagen", err);
                setError(
                    String(err?.message ?? "").includes("403")
                        ? "Kein Zugriff auf dieses To-do."
                        : "Das To-do wurde nicht gefunden.",
                );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id, navigate]);

    if (error) {
        return (
            <div className="vgd vgd-page">
                <div style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "flex-start" }}>
                    <div>{error}</div>
                    <button type="button" className="vgd-btn" onClick={() => navigate("/vorgaenge", { replace: true })}>
                        Zu den Vorgängen
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="vgd vgd-page" style={{ alignItems: "center", paddingTop: 80 }}>
            <Spinner label="To-do wird geöffnet…" />
        </div>
    );
};

export default TodoRedirectPage;
